
import { Component, Mixins, Prop } from 'vue-property-decorator';
import { IdState } from 'vue-virtual-scroller';

const ProviderIcon = () => import('@/components/picture/ProviderIcon.vue');
const PosterQuickActionsREST = () => import('@/components/poster/REST/PosterQuickActionsREST.vue');

import PictureComp, { PictureCompEvent } from '@/components/picture/PictureComp.vue';

import { getTitleSlug } from '@/helpers/title-helper';
import { TitleObjectType } from '@/interfaces/titles';

import type { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import type { ObservableOptions } from '@/interfaces/observable';
import type { NewTitle, Title } from '@/interfaces/titles';

const IdStateMixin = IdState({
	idProp: (vm: Poster) => (vm.title.id ? 'Poster' + vm.title.id : 'Poster-NOID'),
});

@Component({
	name: 'Poster',
	components: { PictureComp, PosterQuickActionsREST, ProviderIcon },
	// virtual scroller persistent state
	idState() {
		return {
			imageLoaded: false,
		};
	},
})
export default class Poster extends Mixins(IdStateMixin) {
	PictureCompEvent = PictureCompEvent;
	getTitleSlug = getTitleSlug;

	@Prop() title: NewTitle | Title;

	@Prop({ default: () => [] }) additionalContexts: () => SnowplowContext[];
	@Prop({ default: () => [] }) providerIds: () => number[];
	@Prop({
		default: () =>
			({
				active: false,
				once: false,
				intersectionCallback: (...args: any[]) => true,
				intersectionOptions: {},
			} as ObservableOptions),
	})
	observableOptions: ObservableOptions;
	// Seasons are marked as watchlisted based on full show instead of episode titles, so we have to pass both.
	@Prop({ default: false }) titleOfShow?: Title;
	@Prop({ default: 'poster' }) imageProfile: string;
	@Prop({ default: false }) showRibbon: boolean;
	@Prop({ default: false }) showQuickActions: boolean;
	@Prop({ default: false }) forceQuickActions: boolean;
	@Prop({ default: false }) hasMoreLikeThis: boolean;
	@Prop({ default: true }) hasWatchNow: boolean;
	@Prop({ default: true }) showTvBadge: boolean;
	@Prop({ default: true }) hideTvBadge: boolean;
	@Prop({ default: true }) lazy: boolean;
	@Prop({ default: '' }) clickSource: string;
	@Prop({ default: '' }) noRadius: string;
	@Prop({ default: false }) smallRadius: boolean;
	@Prop({ default: 'eager' }) loading: string;

	private titleName(title: NewTitle | Title) {
		const name = title.show_title ?? title.title;
		return title.object_type === TitleObjectType.SEASON ? `${name} - ${title.title}` : name;
	}

	private objectType(title: NewTitle | Title) {
		return title.object_type !== TitleObjectType.SEASON ? title.object_type : TitleObjectType.SHOW;
	}

	private objectId(title: NewTitle | Title) {
		const id = title.object_type === TitleObjectType.SEASON ? title.show_id : title.id;
		return id ?? title.id;
	}

	/**
	 * IntersectionObserver's callback:
	 * if element is visible will trigger the callback passed in the props
	 * intersectionCallback()
	 *
	 * @param {any} props
	 * @param {boolean} isVisible
	 * @param {any} entry
	 */
	private onInterception(props: any, isVisible: boolean, entry: any) {
		const { observableOptions } = props;
		if (observableOptions && isVisible) {
			observableOptions.intersectionCallback(isVisible, entry);
		}
	}

	private noRadiusClass(noRadiusProp: string) {
		return ['top', 'right', 'bottom', 'left'].indexOf(noRadiusProp) !== -1
			? `title-poster--no-radius-${noRadiusProp}`
			: '';
	}

	get observable() {
		return (
			this.observableOptions?.active && {
				callback: (isVisible: boolean, entry: any) => this.onInterception(this.$props, isVisible, entry),
				intersection: this.observableOptions.intersectionOptions,
				once: this.observableOptions.once,
				throttle: this.observableOptions.throttle,
			}
		);
	}

	get titleForRibbon(): null | Title | NewTitle {
		if ([TitleObjectType.MOVIE, TitleObjectType.SHOW].includes(this.title.object_type)) {
			return this.title;
		}
		// You can't watchlist season or episode, only full show.
		if ([TitleObjectType.SEASON, TitleObjectType.EPISODE].includes(this.title.object_type)) {
			return this.titleOfShow ?? this.title; // Fallback because for some cases we might not have title available on page. (so we'll have to load it before making a request).
		}
		return null;
	}
}
